import type { retCalc } from '../../types/damage';
import type { Move } from '../../move/move';
import { isRenzokuMove } from '../../move/move';
import { calcKakuteiRansuu, calcRate, calcRateMultiHit, tatamikomi } from './hit';

// 連続技の合算用に必要なヒット回数を解決（可変連続技は最小回数に寄せる）
export const resolveMultiHitCount = (move: Move | null | undefined): number => {
  if (!move || !isRenzokuMove(move)) {
    return 1;
  }
  const minHits = move.min_renzoku ?? 2;
  const maxHits = move.max_renzoku ?? 5;
  if (minHits === maxHits) {
    return minHits;
  }
  // 可変連続技は最小回数に寄せる（詳細表示の基準と合わせる）
  return minHits;
};

const buildMultiHitRandomTable = (
  baseTable: { [damage: number]: number },
  hitCount: number,
): { [damage: number]: number } => {
  if (hitCount <= 1) {
    return { ...baseTable };
  }
  // random_tableは1発分の分布なので、ヒット回数分を畳み込んで総ダメージ分布を作る
  const tables = Array.from({ length: hitCount }, () => ({ ...baseTable }));
  return tatamikomi(tables);
};

export const buildCombinedDamageResultWithHitCounts = (
  details: Array<{ detail: retCalc | null | undefined; hitCount: number }>,
  defenderHp: number,
): retCalc | null => {
  if (defenderHp <= 0) {
    return null;
  }

  const tables = details
    .map(({ detail, hitCount }) => {
      const table = detail?.random_table;
      if (!table || Object.keys(table).length === 0) {
        return null;
      }
      const resolvedHitCount = Math.max(1, Math.floor(hitCount));
      return buildMultiHitRandomTable(table, resolvedHitCount);
    })
    .filter((table): table is { [damage: number]: number } => Boolean(table && Object.keys(table).length > 0));

  if (tables.length === 0) {
    return null;
  }

  // 複数の乱数分布を畳み込んで合算結果を作る
  const combinedTable = tatamikomi([...tables]);
  const damageValues = Object.keys(combinedTable).map((value) => Number(value));

  if (damageValues.length === 0) {
    return null;
  }

  const minDamage = Math.min(...damageValues);
  const maxDamage = Math.max(...damageValues);

  if (maxDamage <= 0) {
    return null;
  }

  const headInfo = calcKakuteiRansuu(defenderHp, minDamage, maxDamage, 1);
  let text = `${headInfo.s}${headInfo.n}発【${minDamage}～${maxDamage}】`;

  if (headInfo.s === '乱数') {
    const rate =
      headInfo.n === 1
        ? calcRate(defenderHp, combinedTable)
        : calcRateMultiHit(defenderHp, combinedTable, headInfo.n);
    text += `\n(${rate}%)`;
  }

  return {
    text,
    min_damage: minDamage,
    max_damage: maxDamage,
    random_table: combinedTable,
  };
};

export const buildCombinedDamageResult = (
  details: Array<retCalc | null | undefined>,
  defenderHp: number,
): retCalc | null =>
  buildCombinedDamageResultWithHitCounts(
    details.map((detail) => ({ detail, hitCount: 1 })),
    defenderHp,
  );
